import {
  Button,
  Badge,
  Text,
  tokens,
  Divider,
  Input,
  Tooltip,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  EditRegular,
  CheckmarkRegular,
  DismissRegular,
  MailRegular,
  DocumentRegular,
  DeleteRegular,
  LinkRegular,
} from '@fluentui/react-icons';
import type { ChecklistDetailDialogProps } from './types';
import { realLinkProps } from '../../components/links/realLinkProps';

export default function ChecklistDetailDialog({
  open,
  onOpenChange,
  selectedChecklist,
  editingField,
  setEditingField,
  editValue,
  setEditValue,
  feedbackSubmitting,
  handleSubmitCorrection,
  receivedItems,
  setReceivedItems,
  deleteConfirmIndex,
  setDeleteConfirmIndex,
  updateReceivedMutation,
  deleteItemMutation,
  deleteChecklistConfirmOpen,
  setDeleteChecklistConfirmOpen,
  deleteChecklistMutation,
  setMatchDialogOpen,
  navigate,
}: ChecklistDetailDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
    >
      <DialogSurface style={{ maxWidth: '700px' }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={() => onOpenChange(false)}
              />
            }
          >
            Checklist Details
          </DialogTitle>
          <DialogContent>
            {selectedChecklist && (
              <div>
                {/* Client Info */}
                <div style={{ marginBottom: '24px' }}>
                  <Text weight="semibold" size={400}>Client Information</Text>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Name</Text>
                      <Text as="p">{selectedChecklist.extractedClientInfo?.name || '-'}</Text>
                    </div>
                    <div>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Spouse</Text>
                      <Text as="p">{selectedChecklist.extractedClientInfo?.spouseName || '-'}</Text>
                    </div>
                    <div>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Address</Text>
                      <Text as="p">
                        {[
                          selectedChecklist.extractedClientInfo?.address,
                          selectedChecklist.extractedClientInfo?.city,
                          selectedChecklist.extractedClientInfo?.state,
                          selectedChecklist.extractedClientInfo?.zip,
                        ].filter(Boolean).join(', ') || '-'}
                      </Text>
                    </div>
                    <div>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>SSN (Last 4)</Text>
                      <Text as="p">{selectedChecklist.extractedClientInfo?.ssnLast4 || '-'}</Text>
                    </div>
                  </div>
                </div>

                <Divider />

                {/* Checklist Items with Feedback */}
                <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text weight="semibold" size={400}>
                      Document Checklist ({selectedChecklist.checklistItems?.length || 0} items)
                    </Text>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      Click edit to correct extraction errors
                    </Text>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    {selectedChecklist.checklistItems?.map((item, i) => {
                      const fieldKey = `checklistItems[${i}].payerName`;
                      const isEditing = editingField === fieldKey;
                      const isReceived = receivedItems.has(i);
                      return (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                            backgroundColor: isReceived ? tokens.colorPaletteGreenBackground1 : 'transparent',
                            opacity: isReceived ? 0.7 : 1,
                          }}
                        >
                          {/* Received Checkbox */}
                          <Checkbox
                            checked={isReceived}
                            disabled={updateReceivedMutation.isPending}
                            onChange={(_, data) => {
                              const newSet = new Set(receivedItems);
                              if (data.checked) {
                                newSet.add(i);
                              } else {
                                newSet.delete(i);
                              }
                              setReceivedItems(newSet);
                              updateReceivedMutation.mutate({
                                checklistId: selectedChecklist.id,
                                itemIndex: i,
                                received: !!data.checked,
                              });
                            }}
                            style={{ marginRight: '8px' }}
                          />

                          {/* Delete Button with Popover */}
                          <Popover
                            open={deleteConfirmIndex === i}
                            onOpenChange={(_, data) => setDeleteConfirmIndex(data.open ? i : null)}
                          >
                            <PopoverTrigger disableButtonEnhancement>
                              <Tooltip content="Remove item" relationship="label">
                                <Button
                                  size="small"
                                  appearance="subtle"
                                  icon={<DeleteRegular style={{ color: tokens.colorPaletteRedForeground1 }} />}
                                  style={{ marginRight: '8px', minWidth: 'auto' }}
                                />
                              </Tooltip>
                            </PopoverTrigger>
                            <PopoverSurface style={{ padding: '12px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Text size={200}>Remove this item?</Text>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <Button
                                    size="small"
                                    appearance="primary"
                                    style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                                    disabled={deleteItemMutation.isPending}
                                    onClick={() => {
                                      deleteItemMutation.mutate({
                                        checklistId: selectedChecklist.id,
                                        itemIndex: i,
                                      });
                                    }}
                                  >
                                    {deleteItemMutation.isPending ? 'Deleting...' : 'Delete'}
                                  </Button>
                                  <Button
                                    size="small"
                                    appearance="subtle"
                                    onClick={() => setDeleteConfirmIndex(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </PopoverSurface>
                          </Popover>

                          {/* Item content */}
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <Badge appearance="outline" style={{ marginRight: '8px' }}>
                              {item.category}
                            </Badge>
                            {isEditing ? (
                              <Input
                                size="small"
                                value={editValue}
                                onChange={(_, data) => setEditValue(data.value)}
                                style={{ width: '200px' }}
                              />
                            ) : (
                              <Text style={{ textDecoration: isReceived ? 'line-through' : 'none' }}>
                                {item.payerName || 'General'}
                              </Text>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {item.priorYearAmount != null && (
                              <Text style={{ color: tokens.colorNeutralForeground3 }}>
                                ${item.priorYearAmount.toLocaleString()}
                              </Text>
                            )}
                            {isEditing ? (
                              <>
                                <Tooltip content="Save correction" relationship="label">
                                  <Button
                                    size="small"
                                    appearance="primary"
                                    icon={<CheckmarkRegular />}
                                    onClick={() => handleSubmitCorrection(fieldKey, item.payerName || '', editValue)}
                                    disabled={feedbackSubmitting}
                                  />
                                </Tooltip>
                                <Tooltip content="Cancel" relationship="label">
                                  <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<DismissRegular />}
                                    onClick={() => { setEditingField(null); setEditValue(''); }}
                                  />
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip content="Edit payer name" relationship="label">
                                <Button
                                  size="small"
                                  appearance="subtle"
                                  icon={<EditRegular />}
                                  onClick={() => { setEditingField(fieldKey); setEditValue(item.payerName || ''); }}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Prior Year Amounts */}
                {selectedChecklist.priorYearAmounts && (
                  <>
                    <Divider style={{ marginTop: '24px' }} />
                    <div style={{ marginTop: '24px' }}>
                      <Text weight="semibold" size={400}>Prior Year Summary</Text>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '12px' }}>
                        <div>
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Total Income</Text>
                          <Text as="p">
                            ${selectedChecklist.priorYearAmounts.totalIncome?.toLocaleString() || '-'}
                          </Text>
                        </div>
                        <div>
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>AGI</Text>
                          <Text as="p">
                            ${selectedChecklist.priorYearAmounts.agi?.toLocaleString() || '-'}
                          </Text>
                        </div>
                        <div>
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Total Tax</Text>
                          <Text as="p">
                            ${selectedChecklist.priorYearAmounts.totalTax?.toLocaleString() || '-'}
                          </Text>
                        </div>
                        <div>
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Refund/Owed</Text>
                          <Text as="p" style={{
                            color: (selectedChecklist.priorYearAmounts.refundOrOwed || 0) < 0
                              ? tokens.colorPaletteGreenForeground1
                              : tokens.colorPaletteRedForeground1
                          }}>
                            ${Math.abs(selectedChecklist.priorYearAmounts.refundOrOwed || 0).toLocaleString()}
                            {(selectedChecklist.priorYearAmounts.refundOrOwed || 0) < 0 ? ' refund' : ' owed'}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Popover
              open={deleteChecklistConfirmOpen}
              onOpenChange={(_, data) => setDeleteChecklistConfirmOpen(data.open)}
            >
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular style={{ color: tokens.colorPaletteRedForeground1 }} />}
                >
                  Delete Checklist
                </Button>
              </PopoverTrigger>
              <PopoverSurface style={{ padding: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Text weight="semibold">Delete this checklist?</Text>
                  <Text size={200}>This will permanently delete the checklist and its PDF. This action cannot be undone.</Text>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button appearance="subtle" onClick={() => setDeleteChecklistConfirmOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      appearance="primary"
                      style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                      disabled={deleteChecklistMutation.isPending}
                      onClick={() => {
                        if (selectedChecklist) {
                          deleteChecklistMutation.mutate(selectedChecklist.id);
                        }
                      }}
                    >
                      {deleteChecklistMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </div>
              </PopoverSurface>
            </Popover>

            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>

            {selectedChecklist?.matchStatus !== 'matched' && (
              <Button
                appearance="primary"
                icon={<LinkRegular />}
                onClick={() => {
                  onOpenChange(false);
                  setMatchDialogOpen(true);
                }}
              >
                Match to Client
              </Button>
            )}

            {selectedChecklist?.matchStatus === 'matched' && selectedChecklist?.clientId && (
              <>
                <Button
                  as="a"
                  appearance="primary"
                  icon={<MailRegular />}
                  {...realLinkProps(`/outreach?clientId=${selectedChecklist.clientId}`, { navigate })}
                >
                  Send Outreach
                </Button>

                {selectedChecklist.linkedTaxReturnId && (
                  <Button
                    as="a"
                    appearance="secondary"
                    icon={<DocumentRegular />}
                    {...realLinkProps(`/tax-returns/${selectedChecklist.linkedTaxReturnId}`, { navigate })}
                  >
                    View Tax Return
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
